import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

const categorySeoDescriptions: Record<string, string> = {
  features: '全面了解91c平台的核心功能与特色亮点，包括数据看板、智能分析、自动化工作流等强大功能模块的详细介绍。',
  tutorials: '详细的91c操作指南与使用教程，从新手入门到高级技巧，覆盖每个使用场景。',
  reviews: '真实用户的使用体验与评价反馈，了解91c在实际业务场景中的表现和用户满意度。',
  versions: '91c各版本更新日志与新功能一览，跟踪最新版本动态和产品路线图。',
  faq: '关于91c的常见疑问解答，包括账号安全、付费方式、功能使用等热门问题。',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const articles = getArticlesByCategory(slug || '');
  const categoryName = categoryMap[slug || ''] || '分类';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${categoryName} - 91c`,
      description: categorySeoDescriptions[slug || ''] || `${categoryName}相关文章和内容`,
      url: `https://91c.com/category/${slug}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91c.com' },
        { '@type': 'ListItem', position: 2, name: categoryName },
      ],
    },
  ];

  return (
    <>
      <SEO
        title={`${categoryName} - 91c`}
        description={categorySeoDescriptions[slug || ''] || `浏览91c${categoryName}相关的所有文章和内容。`}
        keywords={`91c,${categoryName},91c${categoryName}`}
        canonicalUrl={`https://91c.com/category/${slug}`}
        ogTitle={`${categoryName} - 91c`}
        ogDescription={categorySeoDescriptions[slug || ''] || `浏览91c${categoryName}相关的所有文章和内容。`}
        ogUrl={`https://91c.com/category/${slug}`}
        jsonLd={jsonLd}
      />

      <main className="pt-16 min-h-screen">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: categoryName },
              ]}
            />
            <div className="px-4 md:px-6 pb-10">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900">
                {categoryName}
              </h1>
              {currentCategory && (
                <p className="mt-2 text-sm text-foreground-500 max-w-2xl">
                  {currentCategory.description}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
          {/* Category tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {articles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group p-5 bg-background-50 border border-background-200 rounded-lg hover:border-primary-200 transition-all duration-300 flex flex-col"
                >
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-300 line-clamp-2 leading-snug">
                    {article.title}
                  </h3>
                  <p className="mt-2 text-xs text-foreground-500 leading-relaxed line-clamp-3 flex-1">
                    {article.excerpt}
                  </p>
                  <div className="mt-4 pt-3 border-t border-background-100 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-foreground-400">
                      <span>{article.date}</span>
                      <span className="text-foreground-200">·</span>
                      <span>{article.readTime}阅读</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {article.tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 bg-secondary-50 text-secondary-600 text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
                <i className="ri-article-line text-2xl"></i>
              </div>
              <p className="mt-4 text-sm text-foreground-500">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}